import React from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import { TFeature } from 'types';
import Image from 'next/legacy/image';
import { DynamicHeroIcon } from '..';
import { SaneImage } from '../../sanity/sane-image';

type TClerkComponentsFeatureProps = TFeature;

export const ClerkComponentsFeature: React.FC<TClerkComponentsFeatureProps> = ({
  featureTitle,
  featureDesc,
  image,
  imageFileURL,
  items,
  imagePosition,
  bottomCta
}) => (
  <>
    <div className="flex flex-col gap-8 lg:flex-row">
      <div
        className={classNames(
          'flex-1 ',
          imagePosition === 'left' && 'lg:order-2'
        )}
      >
        <h1 className="mb-3 text-2xl font-extrabold leading-9 tracking-tight text-gray-900 lg:text-3xl">
          {featureTitle}
        </h1>

        <p className="mb-10 text-base font-normal leading-6 text-gray-500">
          {featureDesc}
        </p>

        <div className="flex flex-col gap-10">
          {items.map(({ itemTitle, itemDesc, icon }) => (
            <div key={itemTitle} className="flex">
              <div className="mr-4">
                <span className="flex items-center justify-center w-12 h-12 rounded-md bg-clerk-blue">
                  <DynamicHeroIcon
                    icon={icon}
                    className="w-5 h-5 text-white"
                    aria-hidden="true"
                  />
                </span>
              </div>

              <div className={classNames(!itemDesc && 'flex items-center')}>
                <h2
                  className={classNames(
                    'text-lg font-medium leading-6 text-gray-900',
                    itemDesc ? 'mb-2' : 'mb-0'
                  )}
                >
                  {itemTitle}
                </h2>

                {itemDesc && (
                  <p
                    className="text-base font-normal leading-6 text-gray-500"
                    dangerouslySetInnerHTML={{ __html: itemDesc }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        {bottomCta && (
          <div className="mt-8">
            {bottomCta.description && (
              <span className="mr-1 text-gray-500">
                {bottomCta.description}
              </span>
            )}

            {bottomCta.internalHref && (
              <Link legacyBehavior href={bottomCta.internalHref}>
                {/* eslint-disable-next-line */}
                <a className="text-clerk-blue">
                  {bottomCta.cta}
                  {bottomCta.icon && (
                    <DynamicHeroIcon
                      icon={bottomCta.icon}
                      weight="outline"
                      className="relative inline w-5 h-5 bottom-[1px]"
                    />
                  )}
                </a>
              </Link>
            )}

            {bottomCta.externalHref && (
              <a href={bottomCta.internalHref} target="_blank" rel="noopener">
                {bottomCta.cta}
                {bottomCta.icon && (
                  <DynamicHeroIcon
                    icon={bottomCta.icon}
                    weight="outline"
                    className="relative inline w-5 h-5 bottom-[1px]"
                  />
                )}
              </a>
            )}
          </div>
        )}
      </div>

      <div className="relative flex-1">
        {image && (
          <SaneImage
            wrapClassName="h-60 sm:h-80 md:h-96 lg:h-full"
            className="rounded-3xl"
            src={image}
            height={undefined}
            width={undefined}
            layout="fill"
          />
        )}

        {imageFileURL && (
          <div className="h-60 sm:h-80 md:h-96 lg:h-full">
            <Image
              className="rounded-3xl"
              src={imageFileURL}
              alt="dsg"
              layout="fill"
            />
          </div>
        )}
      </div>
    </div>
  </>
);
